import React, { useState, useEffect, useReducer } from 'react';
import { PaymentElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #333;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ExpressCheckoutContainer = styled.div`
  margin-bottom: 2rem;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  color: #888;
  text-transform: uppercase;
  font-size: 0.9rem;
  font-weight: 600;

  hr {
    flex-grow: 1; border: none; border-top: 1px solid #ccc;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #005eb8;
    box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.2);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #005eb8;
    box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.2);
  }
`;

const CheckboxGroup = styled.div`
  margin-top: 2rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CheckboxInput = styled.input`
  margin-top: 0.2rem;
  margin-right: 0.75rem;
  accent-color: #005eb8;
  cursor: pointer;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: #555;
  line-height: 1.4;
  cursor: pointer;

  a {
    color: #005eb8;
    text-decoration: underline;
    font-weight: 600;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const PayButton = styled.button`
  display: block;
  width: 100%;
  padding: 15px;
  background-color: #005eb8;
  color: white;
  text-align: center;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 4px;
  border: none;
  transition: background-color 0.3s;
  margin-top: 2rem;
  cursor: pointer;

  &:hover {
    background-color: #004a94;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d9534f;
  background-color: #f2dede;
  border: 1px solid #ebccd1;
  padding: 10px;
  border-radius: 4px;
  margin-top: 1rem;
  text-align: center;
`;

const initialState = {
    name: '',
    dob: '',
    gender: '',
    email: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
    agreedToTerms: false,
    agreedToMarketing: false,
};

function formReducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'PREFILL_USER_DATA':
            return { ...state, ...action.payload };
        default:
            return state;
    }
}

export default function CheckoutForm({ total, clientSecret }) {
    const stripe = useStripe();
    const elements = useElements();
    const { user, isAuthenticated } = useAuth();

    const [state, dispatch] = useReducer(formReducer, initialState);
    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [emailExistsError, setEmailExistsError] = useState('');
    const [paymentRequest, setPaymentRequest] = useState(null);

    useEffect(() => {
        // Pre-fill form if user is logged in
        if (isAuthenticated && user) {
            const userData = {
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
            };
            if (user.shipping_address && typeof user.shipping_address === 'object') {
                userData.line1 = user.shipping_address.line1 || '';
                userData.line2 = user.shipping_address.line2 || '';
                userData.city = user.shipping_address.city || '';
                userData.state = user.shipping_address.state || '';
                userData.postal_code = user.shipping_address.postal_code || '';
                userData.country = user.shipping_address.country || '';
            }
            // Ensure date is always in YYYY-MM-DD for the HTML5 input
            if (user.dob) {
                const d = new Date(user.dob);
                userData.dob = d.toISOString().split('T')[0];
            }
            if (user.sex) userData.gender = user.sex; // Map user.sex to form's gender field
            dispatch({ type: 'PREFILL_USER_DATA', payload: userData });
        }
        // This effect should only run when the user's authentication status changes.
        // The individual state setters (setName, setEmail, etc.) are not needed in the dependency array
        // as they are guaranteed by React to be stable.
        // To avoid a missing dependency warning, you would typically destructure the setters
        // from your state management solution (like a context or reducer) which is out of scope here.
        // For now, we acknowledge this is a one-time setup effect on auth change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isAuthenticated]);

    useEffect(() => {
        if (stripe && total > 0) {
            const pr = stripe.paymentRequest({
                country: 'GB',
                currency: 'gbp',
                total: {
                    label: 'The Letter Doctor',
                    amount: Math.round(total * 100),
                },
                requestPayerName: true,
                requestPayerEmail: true,
                requestShipping: true, // Enable shipping address collection in express checkouts
            });

            pr.canMakePayment().then(result => {
                if (result) {
                    setPaymentRequest(pr);
                }
            });
        }
    }, [stripe, total]);

    useEffect(() => {
        if (paymentRequest) {
            paymentRequest.on('paymentmethod', async (ev) => {
                // This check is inside the event handler, but stripe instance is stable.
                // It's better to ensure stripe exists before adding the listener.
                if (!stripe || !clientSecret) {
                    // Stripe.js has not yet loaded.
                    // Make sure to disable form submission until Stripe.js has loaded.
                    setMessage("Stripe.js or client secret is not ready.");
                    ev.complete('fail');
                    return;
                }

                // Confirm the PaymentIntent with the payment method from the event.
                const { error } = await stripe.confirmPayment({
                    clientSecret,
                    confirmParams: {
                        payment_method: ev.paymentMethod.id,
                        return_url: `${window.location.origin}/payment-success`
                    },
                });

                if (error) {
                    // Report to the browser that the payment failed.
                    ev.complete('fail');
                    setMessage(error.message || 'An unexpected error occurred.');
                } else {
                    // Report to the browser that the confirmation was successful.
                    ev.complete('success');
                }
            });
        }
    }, [paymentRequest, stripe, clientSecret]);

    const handleEmailBlur = async () => {
        // Don't check email if user is already logged in
        if (isAuthenticated) return;

        if (!state.email || !/^\S+@\S+\.\S+$/.test(state.email)) {
            setEmailExistsError('');
            return;
        }


        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/check-email`, { // eslint-disable-line no-undef
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: state.email }),
            });
            const data = await response.json();
            if (data.exists) {
                setEmailExistsError('An account with this email already exists. Please log in to continue.');
            } else {
                setEmailExistsError('');
            }
        } catch (error) {
            console.error('Error checking email:', error);
            // Don't block the user if the check fails, just log it.
            setEmailExistsError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsLoading(true);

        // Save email to session storage to retrieve on the success page, as it's not available in the payment intent details.
        sessionStorage.setItem('customerEmailForSuccess', state.email);
        sessionStorage.setItem('agreedToMarketing', state.agreedToMarketing);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/payment-success`,
                // Add shipping details to the payment confirmation.
                shipping: {
                    name: state.name,
                    address: {
                        line1: state.line1,
                        line2: state.line2,
                        city: state.city,
                        state: state.state,
                        postal_code: state.postal_code,
                        country: state.country,
                    },
                },
                // By explicitly passing payment_method_data, we ensure that the
                // name, email, and phone from your custom form fields are
                // attached to the payment method. The PaymentElement will still
                // collect the address details from its own fields.
                payment_method_data: {
                    billing_details: {
                        name: state.name,
                        email: state.email,
                        phone: state.phone,
                    },
                },
            },
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error) {
            if (error.type === "card_error" || error.type === "validation_error") {
                setMessage(error.message);
            } else {
                setMessage("An unexpected error occurred.");
            }
        }

        setIsLoading(false);
    };

    return (
        <Form onSubmit={handleSubmit}>
            {paymentRequest && (
                <ExpressCheckoutContainer>
                    <PaymentRequestButtonElement options={{ paymentRequest }} />
                </ExpressCheckoutContainer>
            )}

            {paymentRequest && (
                <OrDivider>
                    <hr />
                    <span style={{ padding: '0 1rem' }}>Or pay with card</span>
                    <hr />
                </OrDivider>
            )}
            <SectionTitle>Your Details</SectionTitle>
            <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" type="text" value={state.name} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'name', value: e.target.value })} required />
            </FormGroup>
            <FormGroup>
                <Label htmlFor="dob">Date of Birth</Label>
                <Input id="dob" type="date" value={state.dob} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'dob', value: e.target.value })} required />
            </FormGroup>
            <FormGroup>
                <Label htmlFor="gender">Gender</Label>
                <Select id="gender" value={state.gender} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'gender', value: e.target.value })} required>
                    <option value="" disabled>Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                </Select>
            </FormGroup>
            <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" value={state.email} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'email', value: e.target.value })} onBlur={handleEmailBlur} required disabled={isAuthenticated} />
                {emailExistsError && <ErrorMessage style={{ marginTop: '0.5rem' }}>{emailExistsError} <Link to="/login">Log In</Link></ErrorMessage>}
            </FormGroup>
            <FormGroup>
                <Label>Shipping Address (for posted items)</Label>
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <Input name="line1" placeholder="Address Line 1" value={state.line1} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'line1', value: e.target.value })} />
                    <Input name="line2" placeholder="Address Line 2 (Optional)" value={state.line2} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'line2', value: e.target.value })} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Input name="city" placeholder="City" value={state.city} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'city', value: e.target.value })} />
                        <Input name="state" placeholder="County/State" value={state.state} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'state', value: e.target.value })} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Input name="postal_code" placeholder="Postcode" value={state.postal_code} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'postal_code', value: e.target.value })} />
                        <Input name="country" placeholder="Country" value={state.country} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'country', value: e.target.value })} />
                    </div>
                </div>
            </FormGroup>
            <FormGroup>
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <Input id="phone" type="tel" value={state.phone} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'phone', value: e.target.value })} />
            </FormGroup>

            <SectionTitle>Payment</SectionTitle>
            <PaymentElement
                options={{
                    layout: { type: 'tabs', defaultCollapsed: false },
                }}
            />
            <CheckboxGroup>
                <CheckboxContainer>
                    <CheckboxInput
                        id="terms"
                        type="checkbox"
                        checked={state.agreedToTerms}
                        onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'agreedToTerms', value: e.target.checked })}
                        required
                    />
                    <CheckboxLabel htmlFor="terms">
                        I confirm I have read and agree to the <Link to="/terms" target="_blank">Terms & Conditions</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                    </CheckboxLabel>
                </CheckboxContainer>
                <CheckboxContainer>
                    <CheckboxInput
                        id="marketing"
                        type="checkbox"
                        checked={state.agreedToMarketing}
                        onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'agreedToMarketing', value: e.target.checked })}
                    />
                    <CheckboxLabel htmlFor="marketing">
                        I would like to receive marketing emails about products and services from The Letter Doctor.
                    </CheckboxLabel>
                </CheckboxContainer>
            </CheckboxGroup>
            <PayButton disabled={isLoading || !stripe || !elements || !state.agreedToTerms || !!emailExistsError}>
                {isLoading ? "Processing..." : "Pay Now"}
            </PayButton>
            {message && <ErrorMessage>{message}</ErrorMessage>}
        </Form>
    );
}